import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { isDemoBrand, demoHash } from '../utils/demoBrand';

const API_BASE = 'https://api.glowytics.com';

// ─── Demo-brand static data (Smart Campaign Engine has no data for demo brands
// and its admin-token endpoints 401, so the panel would be empty) ─────────────
const DEMO_ACTION_TYPES = ['budget_increase', 'budget_decrease', 'pause_campaign', 'bid_adjustment', 'enable_campaign'];
function buildDemoSmartActions(n, pendingOnly) {
  const today = new Date();
  return Array.from({ length: n }).map((_, i) => {
    const h = demoHash(`smart${pendingOnly ? 'p' : 'a'}${i}`);
    const type = DEMO_ACTION_TYPES[h % DEMO_ACTION_TYPES.length];
    const old_value = 5000 + (h % 20000);
    const factor = type.includes('decrease') || type === 'pause_campaign' ? (0.6 + (h % 30) / 100) : (1.1 + (h % 40) / 100);
    const new_value = Math.round(old_value * factor);
    const executed = new Date(today.getTime() - (i * 3 + (h % 6)) * 3600000);
    return {
      id: `demo-action-${pendingOnly ? 'p' : 'a'}-${i}`,
      executed_at: executed.toISOString(),
      platform_campaign_id: `PMax - ${['Fashion', 'Retargeting', 'Prospecting', 'Brand'][h % 4]} ${1 + (h % 5)}`,
      campaign_name: `PMax - ${['Fashion', 'Retargeting', 'Prospecting', 'Brand'][h % 4]}`,
      action_type: type,
      old_value,
      new_value,
      execution_status: pendingOnly ? 'pending' : ['executed', 'executed', 'executed', 'failed'][h % 4],
      approval_status: pendingOnly ? 'pending' : ['approved', 'approved', 'auto_approved'][h % 3],
      reason: [
        'ROAS hedefin %20 üzerinde — bütçe artırıldı',
        'Son 7 günde CPA yükseldi — teklif düşürüldü',
        'Harcama var, dönüşüm yok — duraklatıldı',
        'Güçlü performans — ölçekleniyor',
      ][h % 4],
    };
  });
}
function buildDemoSmartOverview() {
  return { campaignCount: 8, activeRules: 12, pendingApprovals: 5, actionsLast24h: 17 };
}

function getAdminHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-admin-token': localStorage.getItem('adminToken') || '',
  };
}

// ─── Status Badge ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const colorMap = {
    executed: { bg: '#d1fae5', color: '#065f46' },
    pending: { bg: '#fef9c3', color: '#92400e' },
    failed: { bg: '#fee2e2', color: '#991b1b' },
    rolled_back: { bg: '#f3f4f6', color: '#374151' },
    approved: { bg: '#d1fae5', color: '#065f46' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
    active: { bg: '#dbeafe', color: '#1e40af' },
    inactive: { bg: '#f3f4f6', color: '#374151' },
  };
  const style = colorMap[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      borderRadius: 6,
      padding: '2px 10px',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {status ? status.replace(/_/g, ' ') : '—'}
    </span>
  );
}

// ─── Overview Card ────────────────────────────────────────────────────────────
function OverviewCard({ label, value, color }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: '20px 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      flex: 1,
      minWidth: 160,
    }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#111827' }}>{value ?? '—'}</div>
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
      <div style={{
        display: 'inline-block',
        width: 32,
        height: 32,
        border: '3px solid #e5e7eb',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af', fontSize: 14 }}>
      {message || 'No data found.'}
    </div>
  );
}

// ─── Tab: Recent Actions ──────────────────────────────────────────────────────
function ActionsTab({ actions, loading, filters, setFilters, page, setPage }) {
  const ACTION_TYPES = ['budget_change', 'status_change', 'bid_change', 'pause', 'enable'];
  const STATUSES = ['executed', 'pending', 'failed', 'rolled_back'];

  if (loading) return <Spinner />;

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
        <select
          value={filters.actionType || ''}
          onChange={e => setFilters(f => ({ ...f, actionType: e.target.value, page: 1 }))}
          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: '#374151' }}
        >
          <option value="">All Action Types</option>
          {ACTION_TYPES.map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={filters.status || ''}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: '#374151' }}
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.dateFrom || ''}
          onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, page: 1 }))}
          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: '#374151' }}
          placeholder="From"
        />
        <input
          type="date"
          value={filters.dateTo || ''}
          onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, page: 1 }))}
          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: '#374151' }}
          placeholder="To"
        />
        <button
          onClick={() => setFilters({ actionType: '', status: '', dateFrom: '', dateTo: '' })}
          style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: '#374151' }}
        >
          Clear
        </button>
      </div>

      {/* Table */}
      {!actions || actions.length === 0 ? (
        <EmptyState message="No actions found." />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Time', 'Campaign', 'Action Type', 'Old Value', 'New Value', 'Status', 'Approval'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {actions.map((a, i) => (
                  <tr key={a.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: 'white' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {a.executed_at ? new Date(a.executed_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.campaign_name || a.campaign_id || '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>{a.action_type || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{a.old_value ?? '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{a.new_value ?? '—'}</td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge status={a.execution_status} /></td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge status={a.approval_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 14px', fontSize: 13,
                cursor: page <= 1 ? 'not-allowed' : 'pointer',
                background: 'white', color: page <= 1 ? '#d1d5db' : '#374151',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={actions.length < 50}
              style={{
                border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 14px', fontSize: 13,
                cursor: actions.length < 50 ? 'not-allowed' : 'pointer',
                background: 'white', color: actions.length < 50 ? '#d1d5db' : '#374151',
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Pending Approvals ───────────────────────────────────────────────────
function ApprovalsTab({ approvals, loading, onApprove, onReject }) {
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(null); // { id, decision }
  const [bulkDecision, setBulkDecision] = useState(null);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === approvals.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(approvals.map(a => a.id)));
    }
  };

  const handleSingleAction = (id, decision) => {
    setConfirming({ id, decision });
  };

  const confirmAction = () => {
    if (!confirming) return;
    if (confirming.decision === 'approved') {
      onApprove([confirming.id]);
    } else {
      onReject([confirming.id]);
    }
    setConfirming(null);
  };

  const handleBulkAction = (decision) => {
    if (selected.size === 0) return;
    setBulkDecision(decision);
  };

  const confirmBulk = () => {
    if (!bulkDecision) return;
    const ids = Array.from(selected);
    if (bulkDecision === 'approved') {
      onApprove(ids);
    } else {
      onReject(ids);
    }
    setSelected(new Set());
    setBulkDecision(null);
  };

  if (loading) return <Spinner />;
  if (!approvals || approvals.length === 0) return <EmptyState message="No pending approvals." />;

  return (
    <div>
      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14,
          background: '#eff6ff', borderRadius: 8, padding: '10px 16px',
        }}>
          <span style={{ fontSize: 13, color: '#1e40af', fontWeight: 500 }}>{selected.size} selected</span>
          <button
            onClick={() => handleBulkAction('approved')}
            style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
          >
            Bulk Approve
          </button>
          <button
            onClick={() => handleBulkAction('rejected')}
            style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
          >
            Bulk Reject
          </button>
          <button
            onClick={() => setSelected(new Set())}
            style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#374151' }}
          >
            Clear
          </button>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '10px 14px', width: 36 }}>
                <input
                  type="checkbox"
                  checked={selected.size === approvals.length && approvals.length > 0}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              {['Campaign', 'Action', 'Old Value', 'New Value', 'Change %', 'Rule Name', 'Reason', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {approvals.map((a, i) => {
              const changePct = a.old_value && a.new_value && !isNaN(a.old_value) && !isNaN(a.new_value)
                ? (((Number(a.new_value) - Number(a.old_value)) / Number(a.old_value)) * 100).toFixed(1)
                : null;
              return (
                <tr key={a.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: selected.has(a.id) ? '#eff6ff' : 'white' }}
                  onMouseEnter={e => { if (!selected.has(a.id)) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (!selected.has(a.id)) e.currentTarget.style.background = 'white'; }}
                >
                  <td style={{ padding: '10px 14px' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(a.id)}
                      onChange={() => toggleSelect(a.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '10px 14px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.platform_campaign_id || '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>{a.action_type || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>{a.old_value ?? '—'}</td>
                  <td style={{ padding: '10px 14px' }}>{a.new_value ?? '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {changePct != null ? (
                      <span style={{ color: Number(changePct) >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                        {Number(changePct) >= 0 ? '+' : ''}{changePct}%
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>{a.rule_name || '—'}</td>
                  <td style={{ padding: '10px 14px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.reason || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleSingleAction(a.id, 'approved')}
                        style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleSingleAction(a.id, 'rejected')}
                        style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm single action dialog */}
      {confirming && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10, color: '#111827' }}>
              Confirm {confirming.decision === 'approved' ? 'Approval' : 'Rejection'}
            </div>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
              Are you sure you want to <strong>{confirming.decision === 'approved' ? 'approve' : 'reject'}</strong> this action?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirming(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              <button
                onClick={confirmAction}
                style={{
                  background: confirming.decision === 'approved' ? '#10b981' : '#ef4444',
                  color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
                }}
              >
                {confirming.decision === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm bulk action dialog */}
      {bulkDecision && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 380, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10, color: '#111827' }}>
              Bulk {bulkDecision === 'approved' ? 'Approve' : 'Reject'} {selected.size} Actions
            </div>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
              Are you sure you want to <strong>{bulkDecision === 'approved' ? 'approve' : 'reject'}</strong> {selected.size} selected action(s)?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setBulkDecision(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Cancel</button>
              <button
                onClick={confirmBulk}
                style={{
                  background: bulkDecision === 'approved' ? '#10b981' : '#ef4444',
                  color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  const bg = type === 'error' ? '#fee2e2' : '#d1fae5';
  const color = type === 'error' ? '#991b1b' : '#065f46';
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 10000,
      background: bg, color, borderRadius: 10, padding: '12px 20px',
      fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 360,
    }}>
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color, fontWeight: 700, fontSize: 15 }}>✕</button>
    </div>
  );
}

// ─── Confidence Bar ───────────────────────────────────────────────────────────
function ConfidenceBar({ value }) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const color = pct >= 70 ? '#059669' : pct >= 40 ? '#d97706' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 99 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 12, color, fontWeight: 600, minWidth: 30 }}>{pct}</span>
    </div>
  );
}

// ─── Collapsible Text ─────────────────────────────────────────────────────────
function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 12, fontWeight: 500, padding: 0 }}
      >
        {open ? '▲' : '▶'} {label}
      </button>
      {open && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}

// ─── JSON Viewer ──────────────────────────────────────────────────────────────
function JsonViewer({ data }) {
  if (!data) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  let str;
  try { str = JSON.stringify(data, null, 2); } catch { str = String(data); }
  return (
    <pre style={{
      background: '#f3f4f6', borderRadius: 6, padding: '8px 10px',
      fontSize: 11, color: '#374151', margin: 0,
      maxHeight: 160, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    }}>{str}</pre>
  );
}

// ─── KeyValue List ────────────────────────────────────────────────────────────
function KeyValueList({ data }) {
  if (!data || typeof data !== 'object') return <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
      {Object.entries(data).map(([k, v]) => (
        <span key={k} style={{ fontSize: 11, color: '#374151' }}>
          <span style={{ fontWeight: 600, color: '#6b7280' }}>{k}:</span> {String(v)}
        </span>
      ))}
    </div>
  );
}

// ─── Tab 4: Limit Recommendations (Oneriler) ──────────────────────────────────
function RecommendationsTab({ brandId }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // { rec, action: 'accept'|'dismiss' }
  const [applyValue, setApplyValue] = useState('');
  const [modalNote, setModalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchRecs = useCallback(() => {
    if (!brandId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/admin/smart/limit-recommendations?brandId=${brandId}&status=pending`, {
      headers: getAdminHeaders(), credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        setRecs(data.data || data.recommendations || data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [brandId]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  const openModal = (rec, action) => {
    setModal({ rec, action });
    setApplyValue(action === 'accept' ? String(rec.suggested_value ?? '') : '');
    setModalNote('');
  };

  const closeModal = () => { setModal(null); setApplyValue(''); setModalNote(''); };

  const handleSubmit = async () => {
    if (!modal) return;
    setSubmitting(true);
    const { rec, action } = modal;
    const recBrandId = rec.brand_id || rec.brandId;
    const body = action === 'accept'
      ? { decision: 'accepted', apply_value: applyValue !== '' ? Number(applyValue) : undefined, note: modalNote }
      : { decision: 'dismissed', note: modalNote };
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/smart/brands/${recBrandId}/limit-recommendations/${rec.id}`,
        { method: 'PATCH', headers: getAdminHeaders(), credentials: 'include', body: JSON.stringify(body) }
      );
      if (!res.ok) throw new Error((await res.json()).message || 'Request failed');
      setRecs(prev => prev.filter(r => r.id !== rec.id));
      showToast(action === 'accept' ? 'Oneri kabul edildi.' : 'Oneri reddedildi.');
      closeModal();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner />;
  if (!recs || recs.length === 0) return <EmptyState message="Su an bekleyen oneri yok." />;

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {recs.map(rec => {
          const changePct = rec.change_pct != null
            ? rec.change_pct
            : (rec.current_value && rec.suggested_value
              ? (((rec.suggested_value - rec.current_value) / rec.current_value) * 100).toFixed(1)
              : null);
          return (
            <div key={rec.id} style={{
              background: '#f9fafb', borderRadius: 12, padding: '20px 24px',
              border: '1px solid #e5e7eb', position: 'relative',
            }}>
              {/* Header row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: 14, marginBottom: 4 }}>
                    {rec.brand_name || rec.brandName || '—'}
                    {rec.campaign_name && (
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}> / {rec.campaign_name}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    Tip: <span style={{ fontWeight: 500, color: '#374151' }}>{rec.recommendation_type || 'budget_ceiling_increase'}</span>
                  </div>
                </div>

                {/* Value change */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Mevcut</div>
                    <div style={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>{rec.current_value ?? '—'}</div>
                  </div>
                  <div style={{ fontSize: 20, color: '#059669' }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Onerilen</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#059669' }}>{rec.suggested_value ?? '—'}</div>
                  </div>
                  {changePct != null && (
                    <div style={{
                      background: '#d1fae5', color: '#065f46', borderRadius: 6,
                      padding: '4px 8px', fontSize: 12, fontWeight: 700,
                    }}>
                      +{changePct}%
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => openModal(rec, 'accept')}
                    style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Kabul Et
                  </button>
                  <button
                    onClick={() => openModal(rec, 'dismiss')}
                    style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
                  >
                    Reddet
                  </button>
                </div>
              </div>

              {/* Confidence + dates */}
              <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: '10px 28px', alignItems: 'center' }}>
                <div style={{ minWidth: 180 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Guven Skoru</div>
                  <ConfidenceBar value={rec.confidence_score} />
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  Olusturulma: <span style={{ color: '#374151' }}>{rec.created_at ? new Date(rec.created_at).toLocaleString('tr-TR') : '—'}</span>
                </div>
                {rec.expires_at && (
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    Son Gecerlilik: <span style={{ color: '#374151' }}>{new Date(rec.expires_at).toLocaleString('tr-TR')}</span>
                  </div>
                )}
              </div>

              {/* Expandable: reasoning + supporting metrics */}
              <div style={{ marginTop: 14 }}>
                <Collapsible label="Detaylar">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                    {rec.reasoning && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Gerekce</div>
                        <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>{rec.reasoning}</div>
                      </div>
                    )}
                    {rec.supporting_metrics && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Destekleyici Metrikler</div>
                        <KeyValueList data={rec.supporting_metrics} />
                      </div>
                    )}
                  </div>
                </Collapsible>
              </div>
            </div>
          );
        })}
      </div>

      {/* Accept / Dismiss modal */}
      {modal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: 'white', borderRadius: 14, padding: 28, maxWidth: 420, width: '92%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, color: '#111827' }}>
              {modal.action === 'accept' ? 'Oneriyi Kabul Et' : 'Oneriyi Reddet'}
            </div>
            {modal.action === 'accept' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Uygulanacak Deger</label>
                <input
                  type="number"
                  value={applyValue}
                  onChange={e => setApplyValue(e.target.value)}
                  style={{ display: 'block', marginTop: 6, width: '100%', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 12px', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
            )}
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Not (Opsiyonel)</label>
              <textarea
                value={modalNote}
                onChange={e => setModalNote(e.target.value)}
                rows={3}
                placeholder="Aciklama ekleyin..."
                style={{ display: 'block', marginTop: 6, width: '100%', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={{ background: '#f3f4f6', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>Iptal</button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  background: modal.action === 'accept' ? '#10b981' : '#ef4444',
                  color: 'white', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13,
                  cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600,
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Isleniyor...' : modal.action === 'accept' ? 'Kabul Et' : 'Reddet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 5: Product Set Intelligence ─────────────────────────────────────────
function ProductSetTab({ brandId }) {
  const [productSets, setProductSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState({}); // { [productSetId]: true }
  const [toast, setToast] = useState(null);
  const [geminiError, setGeminiError] = useState(false);
  const [expandedSet, setExpandedSet] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchProductSets = useCallback(() => {
    if (!brandId) return;
    setLoading(true);
    setGeminiError(false);
    fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/product-sets/profiles?latestOnly=true`, {
      headers: getAdminHeaders(), credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        setProductSets(data.data || data.productSets || data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    fetchProductSets();
  }, [fetchProductSets]);

  const handleAnalyze = async (productSetId, force = false) => {
    setAnalyzing(prev => ({ ...prev, [productSetId]: true }));
    setGeminiError(false);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/smart/brands/${brandId}/product-sets/${productSetId}/analyze`,
        { method: 'POST', headers: getAdminHeaders(), credentials: 'include', body: JSON.stringify({ force }) }
      );
      if (res.status === 501) {
        setGeminiError(true);
        return;
      }
      if (!res.ok) throw new Error((await res.json()).message || 'Analiz basarisiz');
      const data = await res.json();
      const profile = data.data || data;
      setProductSets(prev => {
        const idx = prev.findIndex(ps => ps.product_set_id === productSetId || ps.productSetId === productSetId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...profile, _analyzed: true };
          return next;
        }
        return [...prev, { ...profile, _analyzed: true }];
      });
      setExpandedSet(productSetId);
      showToast('Analiz tamamlandi.');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setAnalyzing(prev => ({ ...prev, [productSetId]: false }));
    }
  };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Gemini error banner */}
      {geminiError && (
        <div style={{
          background: '#fef3c7', color: '#92400e', borderRadius: 10,
          padding: '12px 16px', marginBottom: 18, fontSize: 13, fontWeight: 500,
          border: '1px solid #fcd34d',
        }}>
          Gemini API key yapilandirilmamis. Analiz yapilamadi.
        </div>
      )}

      {loading && <Spinner />}

      {!loading && productSets.length === 0 && (
        <EmptyState message="Bu brand icin product set bulunamadi." />
      )}

      {!loading && productSets.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {productSets.map(ps => {
            const psId = ps.product_set_id || ps.productSetId || ps.id;
            const isExpanded = expandedSet === psId;
            const isAnalyzing = !!analyzing[psId];
            const hasAnalysis = !!(ps.buyer_persona || ps.suggested_audiences || ps._analyzed);
            return (
              <div key={psId} style={{
                background: '#f9fafb', borderRadius: 12, padding: '20px 24px',
                border: '1px solid #e5e7eb',
              }}>
                {/* Card header */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: 14, marginBottom: 3 }}>
                      {ps.product_set_name || ps.productSetName || ps.name || psId}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>ID: {psId}</div>
                    {ps.analyzed_at && (
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                        Son analiz: {new Date(ps.analyzed_at).toLocaleString('tr-TR')}
                      </div>
                    )}
                    {!ps.analyzed_at && (
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Henuz analiz edilmedi</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {isAnalyzing && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6366f1' }}>
                        <div style={{
                          width: 14, height: 14, border: '2px solid #c7d2fe', borderTop: '2px solid #6366f1',
                          borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                        }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        Analiz ediliyor...
                      </div>
                    )}
                    {!isAnalyzing && (
                      <>
                        <button
                          onClick={() => handleAnalyze(psId, false)}
                          style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                        >
                          Analiz Et
                        </button>
                        <button
                          onClick={() => handleAnalyze(psId, true)}
                          style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
                        >
                          Yeniden Analiz
                        </button>
                      </>
                    )}
                    {hasAnalysis && !isAnalyzing && (
                      <button
                        onClick={() => setExpandedSet(prev => prev === psId ? null : psId)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 12, fontWeight: 500 }}
                      >
                        {isExpanded ? 'Gizle ▲' : 'Sonucu Goster ▼'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded analysis result */}
                {isExpanded && hasAnalysis && (
                  <div style={{ marginTop: 18, borderTop: '1px solid #e5e7eb', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Buyer Persona */}
                    {ps.buyer_persona && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Alici Personas</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', fontSize: 13, color: '#4b5563' }}>
                          {ps.buyer_persona.age_range && <span><span style={{ color: '#9ca3af' }}>Yas:</span> {ps.buyer_persona.age_range}</span>}
                          {ps.buyer_persona.gender && <span><span style={{ color: '#9ca3af' }}>Cinsiyet:</span> {ps.buyer_persona.gender}</span>}
                          {ps.buyer_persona.lifestyle && <span><span style={{ color: '#9ca3af' }}>Yasam tarzi:</span> {ps.buyer_persona.lifestyle}</span>}
                        </div>
                        {ps.buyer_persona.interests && ps.buyer_persona.interests.length > 0 && (
                          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {ps.buyer_persona.interests.map((intr, i) => (
                              <span key={i} style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>{intr}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Category tags */}
                    {ps.category_tags && ps.category_tags.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Kategori Etiketleri</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {ps.category_tags.map((tag, i) => (
                            <span key={i} style={{ background: '#dbeafe', color: '#1e40af', borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Price stats */}
                    {ps.price_stats && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Fiyat Istatistikleri</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', fontSize: 13, color: '#4b5563' }}>
                          {ps.price_stats.min != null && <span><span style={{ color: '#9ca3af' }}>Min:</span> {ps.price_stats.min}</span>}
                          {ps.price_stats.max != null && <span><span style={{ color: '#9ca3af' }}>Max:</span> {ps.price_stats.max}</span>}
                          {ps.price_stats.median != null && <span><span style={{ color: '#9ca3af' }}>Medyan:</span> {ps.price_stats.median}</span>}
                        </div>
                      </div>
                    )}

                    {/* Overall confidence */}
                    {ps.overall_confidence != null && (
                      <div style={{ maxWidth: 280 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Genel Guven Skoru</div>
                        <ConfidenceBar value={ps.overall_confidence} />
                        {ps.overall_reasoning && (
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>{ps.overall_reasoning}</div>
                        )}
                      </div>
                    )}

                    {/* Suggested audiences */}
                    {ps.suggested_audiences && ps.suggested_audiences.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Onerilen Kitlalar ({ps.suggested_audiences.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {ps.suggested_audiences.map((aud, i) => {
                            const typeColors = {
                              interest: { bg: '#d1fae5', color: '#065f46' },
                              lookalike: { bg: '#dbeafe', color: '#1e40af' },
                              custom: { bg: '#ede9fe', color: '#5b21b6' },
                              behavioral: { bg: '#fef9c3', color: '#92400e' },
                            };
                            const tc = typeColors[aud.type] || { bg: '#f3f4f6', color: '#374151' };
                            return (
                              <div key={i} style={{ background: 'white', borderRadius: 10, padding: '14px 16px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                                  <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', flex: 1 }}>{aud.name}</div>
                                  <span style={{ background: tc.bg, color: tc.color, borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{aud.type}</span>
                                  <button
                                    disabled
                                    title="Campaign Detail panelinden ekle"
                                    style={{
                                      background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb',
                                      borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'not-allowed', fontWeight: 500,
                                    }}
                                  >
                                    Apply to Campaign
                                  </button>
                                </div>
                                <div style={{ maxWidth: 280, marginBottom: 6 }}>
                                  <ConfidenceBar value={aud.confidence_score} />
                                </div>
                                {aud.reasoning && (
                                  <Collapsible label="Gerekce">
                                    <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5, marginTop: 4 }}>{aud.reasoning}</div>
                                  </Collapsible>
                                )}
                                {aud.meta_targeting_spec && (
                                  <div style={{ marginTop: 8 }}>
                                    <Collapsible label="Meta Targeting Spec (JSON)">
                                      <div style={{ marginTop: 4 }}>
                                        <JsonViewer data={aud.meta_targeting_spec} />
                                      </div>
                                    </Collapsible>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab 6: Rule Effectiveness ────────────────────────────────────────────────
function RuleEffectivenessTab({ brandId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRules = useCallback(() => {
    if (!brandId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/rule-effectiveness`, {
      headers: getAdminHeaders(), credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        setRules(data.data || data.rules || data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const scoreColor = (score) => {
    if (score >= 70) return '#059669';
    if (score >= 40) return '#d97706';
    return '#dc2626';
  };

  function OutcomeBar({ rule }) {
    const total = (rule.improved_count || 0) + (rule.neutral_count || 0) + (rule.worsened_count || 0);
    if (total === 0) return <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>;
    const impPct = ((rule.improved_count || 0) / total * 100).toFixed(1);
    const neuPct = ((rule.neutral_count || 0) / total * 100).toFixed(1);
    const worPct = ((rule.worsened_count || 0) / total * 100).toFixed(1);
    return (
      <div style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', minWidth: 80 }} title={`Improved: ${impPct}% / Neutral: ${neuPct}% / Worsened: ${worPct}%`}>
        {Number(impPct) > 0 && <div style={{ width: `${impPct}%`, background: '#059669' }} />}
        {Number(neuPct) > 0 && <div style={{ width: `${neuPct}%`, background: '#9ca3af' }} />}
        {Number(worPct) > 0 && <div style={{ width: `${worPct}%`, background: '#dc2626' }} />}
      </div>
    );
  }

  if (loading) return <Spinner />;
  if (rules.length === 0) return <EmptyState message="Bu brand icin kural etkinlik verisi yok." />;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            {['Kural Adi', 'Tier', 'Skor', 'Evaluasyon', 'Improved', 'Neutral', 'Worsened', 'Son Tetiklenme', 'Dagilim'].map(h => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rules.map((rule, i) => {
            const sc = rule.effectiveness_score ?? rule.score ?? 0;
            const col = scoreColor(sc);
            return (
              <tr key={rule.id || rule.rule_id || i}
                style={{ borderBottom: '1px solid #f3f4f6', background: 'white' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <td style={{ padding: '10px 14px', fontWeight: 500, color: '#111827' }}>
                  {rule.rule_name || rule.name || '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {rule.tier != null ? (
                    <span style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                      Tier {rule.tier}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: col }}>{sc}</span>
                    <div style={{ width: 36, height: 6, background: '#e5e7eb', borderRadius: 99 }}>
                      <div style={{ width: `${Math.min(100, sc)}%`, height: '100%', background: col, borderRadius: 99 }} />
                    </div>
                  </div>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                  {rule.total_evaluations ?? rule.evaluations ?? '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#059669', fontWeight: 500, textAlign: 'center' }}>
                  {rule.improved_count ?? '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#6b7280', textAlign: 'center' }}>
                  {rule.neutral_count ?? '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#dc2626', fontWeight: 500, textAlign: 'center' }}>
                  {rule.worsened_count ?? '—'}
                </td>
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                  {rule.last_triggered_at
                    ? new Date(rule.last_triggered_at).toLocaleString('tr-TR')
                    : '—'}
                </td>
                <td style={{ padding: '10px 14px', minWidth: 100 }}>
                  <OutcomeBar rule={rule} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminSmartCampaigns() {
  const { selectedBrand } = useAuth();
  const [activeTab, setActiveTab] = useState('actions');
  const [overview, setOverview] = useState(null);
  const [actions, setActions] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingActions, setLoadingActions] = useState(false);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ actionType: '', status: '', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Early return if no brand selected
  if (!selectedBrand?.id) {
    return (
      <div style={{
        padding: 48,
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 12 }}>Brand secilmedi</h1>
        <p style={{ fontSize: 14, color: '#6b7280' }}>Sidebar'dan bir brand secin.</p>
      </div>
    );
  }

  return <AdminSmartCampaignsInner
    selectedBrand={selectedBrand}
    activeTab={activeTab}
    setActiveTab={setActiveTab}
    overview={overview}
    setOverview={setOverview}
    actions={actions}
    setActions={setActions}
    pendingApprovals={pendingApprovals}
    setPendingApprovals={setPendingApprovals}
    loadingOverview={loadingOverview}
    setLoadingOverview={setLoadingOverview}
    loadingActions={loadingActions}
    setLoadingActions={setLoadingActions}
    loadingApprovals={loadingApprovals}
    setLoadingApprovals={setLoadingApprovals}
    error={error}
    setError={setError}
    filters={filters}
    setFilters={setFilters}
    page={page}
    setPage={setPage}
    autoRefresh={autoRefresh}
    setAutoRefresh={setAutoRefresh}
  />;
}

// ─── Engine health banner ────────────────────────────────────────────────────
//
// Reads /api/admin/smart/engine-health, which serves the public.smart_engine_runs
// ledger. This exists because the engine previously ran on cron across 172 brands
// and evaluated exactly zero rules for months while logging summaries that read
// like success ("brands_ok=47 brands_err=0 | evaluated_rules=0"). The backend now
// computes a health verdict per run; this strip is what makes that verdict
// impossible to miss.
//
// The three states are deliberately distinct:
//   ok    — configs were seen AND rules were evaluated
//   idle  — nothing enrolled yet. Honest, not a fault.
//   alarm — the engine is running but not working, or ran blind
function EngineHealthBanner({ isDemo }) {
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/admin/smart/engine-health?limit=12`, {
      headers: getAdminHeaders(), credentials: 'include',
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { if (!cancelled) setHealth(d.data || d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [isDemo]);

  if (isDemo || failed || !health) return null;

  const STYLES = {
    ok:      { bg: '#ecfdf5', border: '#6ee7b7', color: '#065f46', icon: '✓', label: 'Motor çalışıyor' },
    idle:    { bg: '#f8fafc', border: '#cbd5e1', color: '#334155', icon: '○', label: 'Motor boşta' },
    alarm:   { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', icon: '!', label: 'MOTOR ÇALIŞMIYOR' },
    unknown: { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', icon: '?', label: 'Ölçülemiyor' },
  };
  const s = STYLES[health.verdict] || STYLES.unknown;

  // Surface the actual reason, per tier, rather than a bare colour.
  const tierRows = Object.values(health.latestByTier || {});
  const alarmReasons = tierRows
    .filter(r => r.health === 'alarm' || r.health === 'aborted_drift')
    .map(r => `${r.tier}: ${r.alarmReason}`);

  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontWeight: 800, fontSize: 14, width: 20, height: 20, lineHeight: '20px',
          textAlign: 'center', borderRadius: '50%', background: s.border, color: s.color,
        }}>{s.icon}</span>
        <strong style={{ fontWeight: 700 }}>{s.label}</strong>
        {health.gate && (
          <span style={{
            background: health.gate.dryRun ? '#e0e7ff' : '#fee2e2',
            color: health.gate.dryRun ? '#3730a3' : '#991b1b',
            borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
          }}>
            {health.gate.dryRun ? 'DRY-RUN — Meta\'ya hiçbir şey gitmiyor' : `CANLI: ${health.gate.liveBrands || '(marka yok)'}`}
          </span>
        )}
        {health.unfinishedRuns > 0 && (
          <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
            {health.unfinishedRuns} tur yarıda kaldı
          </span>
        )}
      </div>

      {health.message && (
        <div style={{ marginTop: 6, opacity: 0.9 }}>{health.message}</div>
      )}

      {alarmReasons.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 22 }}>
          {alarmReasons.map((r, i) => <li key={i} style={{ marginTop: 2 }}>{r}</li>)}
        </ul>
      )}

      {tierRows.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, opacity: 0.85 }}>
          {tierRows.map(r => (
            <span key={r.tier}>
              <strong>{r.tier}</strong>{' '}
              {r.configsSeen} kampanya · {r.rulesEvaluated} kural ·{' '}
              {r.actionsQueued} onayda · {r.actionsExecuted} uygulandı
              {r.metricsUnavailable > 0 && ` · ${r.metricsUnavailable} metriksiz`}
              {r.startedAt && ` · ${new Date(r.startedAt).toLocaleString('tr-TR')}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminSmartCampaignsInner({
  selectedBrand,
  activeTab, setActiveTab,
  overview, setOverview,
  actions, setActions,
  pendingApprovals, setPendingApprovals,
  loadingOverview, setLoadingOverview,
  loadingActions, setLoadingActions,
  loadingApprovals, setLoadingApprovals,
  error, setError,
  filters, setFilters,
  page, setPage,
  autoRefresh, setAutoRefresh,
}) {
  const brandId = selectedBrand.id;

  // Fetch brand-scoped overview/dashboard
  const fetchOverview = useCallback(() => {
    setLoadingOverview(true);
    fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/dashboard`, { headers: getAdminHeaders(), credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const d = data.data || data;
        const empty = !d || (d.campaignCount == null && d.campaign_count == null && !d.configs);
        setOverview((empty && isDemoBrand(selectedBrand)) ? buildDemoSmartOverview() : d);
        setLoadingOverview(false);
      })
      .catch(err => {
        if (isDemoBrand(selectedBrand)) { setOverview(buildDemoSmartOverview()); setError(null); }
        else setError(err.message);
        setLoadingOverview(false);
      });
  }, [brandId, selectedBrand, setLoadingOverview, setOverview, setError]);

  // Build actions query string (brand-scoped)
  const buildActionsUrl = useCallback(() => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (filters.actionType) params.set('action_type', filters.actionType);
    if (filters.status) params.set('status', filters.status);
    if (filters.dateFrom) params.set('from', filters.dateFrom);
    if (filters.dateTo) params.set('to', filters.dateTo);
    return `${API_BASE}/api/admin/smart/brands/${brandId}/actions?${params.toString()}`;
  }, [brandId, filters, page]);

  // Fetch actions
  const fetchActions = useCallback(() => {
    setLoadingActions(true);
    fetch(buildActionsUrl(), { headers: getAdminHeaders(), credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const arr = data.data || data.actions || (Array.isArray(data) ? data : []) || [];
        setActions((arr.length === 0 && isDemoBrand(selectedBrand)) ? buildDemoSmartActions(18, false) : arr);
        setLoadingActions(false);
      })
      .catch(() => {
        if (isDemoBrand(selectedBrand)) setActions(buildDemoSmartActions(18, false));
        setLoadingActions(false);
      });
  }, [buildActionsUrl, selectedBrand, setLoadingActions, setActions]);

  // Fetch pending approvals (brand-scoped)
  const fetchApprovals = useCallback(() => {
    setLoadingApprovals(true);
    const params = new URLSearchParams({ status: 'pending', limit: 100 });
    fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/actions?${params.toString()}`, { headers: getAdminHeaders(), credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const arr = data.data || data.actions || (Array.isArray(data) ? data : []) || [];
        setPendingApprovals((arr.length === 0 && isDemoBrand(selectedBrand)) ? buildDemoSmartActions(5, true) : arr);
        setLoadingApprovals(false);
      })
      .catch(() => {
        if (isDemoBrand(selectedBrand)) setPendingApprovals(buildDemoSmartActions(5, true));
        setLoadingApprovals(false);
      });
  }, [brandId, selectedBrand, setLoadingApprovals, setPendingApprovals]);

  // Initial load — retrigger when brandId changes
  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Tab-based fetch
  useEffect(() => {
    if (activeTab === 'actions') fetchActions();
    if (activeTab === 'approvals') fetchApprovals();
  }, [activeTab, fetchActions, fetchApprovals]);

  // Re-fetch actions when filters or page change
  useEffect(() => {
    if (activeTab === 'actions') fetchActions();
  }, [filters, page, activeTab, fetchActions]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchOverview();
      if (activeTab === 'actions') fetchActions();
      if (activeTab === 'approvals') fetchApprovals();
    }, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, fetchOverview, fetchActions, fetchApprovals]);

  const handleRefresh = () => {
    fetchOverview();
    if (activeTab === 'actions') fetchActions();
    if (activeTab === 'approvals') fetchApprovals();
  };

  const handleApprove = async (ids) => {
    try {
      await Promise.all(ids.map(id =>
        fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/actions/${id}/approve`, {
          method: 'PATCH',
          headers: getAdminHeaders(),
          credentials: 'include',
          body: JSON.stringify({ decision: 'approved' }),
        })
      ));
      fetchApprovals();
      fetchOverview();
    } catch (err) {
      setError('Approval failed: ' + err.message);
    }
  };

  const handleReject = async (ids) => {
    try {
      await Promise.all(ids.map(id =>
        fetch(`${API_BASE}/api/admin/smart/brands/${brandId}/actions/${id}/approve`, {
          method: 'PATCH',
          headers: getAdminHeaders(),
          credentials: 'include',
          body: JSON.stringify({ decision: 'rejected' }),
        })
      ));
      fetchApprovals();
      fetchOverview();
    } catch (err) {
      setError('Rejection failed: ' + err.message);
    }
  };

  const TABS = [
    { key: 'actions', label: 'Recent Actions' },
    { key: 'approvals', label: `Pending Approvals${overview?.pendingApprovals ? ` (${overview.pendingApprovals})` : ''}` },
    { key: 'recommendations', label: 'Oneriler' },
    { key: 'productsets', label: 'Product Set Intelligence' },
    { key: 'ruleeffectiveness', label: 'Rule Effectiveness' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
            {selectedBrand.name} — Smart Campaign Engine
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Autonomous campaign management monitoring dashboard</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => setAutoRefresh(a => !a)}
            style={{
              background: autoRefresh ? '#6366f1' : '#f3f4f6',
              color: autoRefresh ? 'white' : '#374151',
              border: 'none', borderRadius: 6, padding: '7px 16px',
              fontSize: 13, cursor: 'pointer', fontWeight: 500,
            }}
          >
            {autoRefresh ? 'Auto-refresh: ON' : 'Auto-refresh: OFF'}
          </button>
          <button
            onClick={handleRefresh}
            style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* Engine health — the run ledger only helps if a human actually sees it */}
      <EngineHealthBanner isDemo={isDemoBrand(selectedBrand)} />

      {/* Overview Cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        {loadingOverview ? (
          [1, 2, 3, 4].map(k => (
            <div key={k} style={{ background: 'white', borderRadius: 12, padding: '20px 24px', flex: 1, minWidth: 160, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ background: '#f3f4f6', borderRadius: 4, height: 14, width: '60%', marginBottom: 10 }} />
              <div style={{ background: '#f3f4f6', borderRadius: 4, height: 28, width: '40%' }} />
            </div>
          ))
        ) : (
          <>
            <OverviewCard label="Managed Campaigns" value={overview?.campaignCount ?? overview?.campaign_count ?? overview?.configs?.length} color="#6366f1" />
            <OverviewCard label="Active Rules" value={overview?.activeRules ?? overview?.active_rules} color="#059669" />
            <OverviewCard label="Pending Approvals" value={overview?.pendingApprovals ?? overview?.pending_approvals} color="#d97706" />
            <OverviewCard label="Actions (24h)" value={overview?.actionsLast24h ?? overview?.actions_24h} color="#0891b2" />
          </>
        )}
      </div>

      {/* Tab Selector */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 24 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -2,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#6366f1' : '#6b7280',
              cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {activeTab === 'actions' && (
          <ActionsTab
            actions={actions}
            loading={loadingActions}
            filters={filters}
            setFilters={setFilters}
            page={page}
            setPage={setPage}
          />
        )}
        {activeTab === 'approvals' && (
          <ApprovalsTab
            approvals={pendingApprovals}
            loading={loadingApprovals}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
        {activeTab === 'recommendations' && (
          <RecommendationsTab brandId={brandId} />
        )}
        {activeTab === 'productsets' && (
          <ProductSetTab brandId={brandId} />
        )}
        {activeTab === 'ruleeffectiveness' && (
          <RuleEffectivenessTab brandId={brandId} />
        )}
      </div>
    </div>
  );
}
